import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import Text from '@/components/Typography/Text';
import SettingsSection from '@/components/SettingsSection';
import { SHORTCUTS } from '@/hooks/useKeyboardShortcuts';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
  },
  section: {
    marginBottom: 24,
  },
  shortcutRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    width: '100%',
    flexWrap: 'wrap',
    gap: 12
  },
  shortcutKey: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  keysContainer: {
    flexDirection: 'row',
    gap: 8,
    marginLeft: 'auto'
  }
});

function ShortcutRow({ shortcut, displayKeys, isFirst, colors, styles, t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      style={[
        styles.shortcutRow,
        isFirst && { paddingTop: 0 }
      ]}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
    >
      {(state) => {
        const isHovered = hovered || state.hovered;
        return (
          <>
            <Text size="n" weight={isHovered ? "bold" : "normal"} color="secondary">{t(shortcut.action)}</Text>
            <View style={styles.keysContainer}>
              {displayKeys.map((key, keyIndex) => (
                <View
                  key={`shortcut-key-${keyIndex}`}
                  style={[
                    styles.shortcutKey,
                    isHovered && { backgroundColor: colors.solid_color_secondary_dark }
                  ]}
                >
                  <Text size="n" weight={isHovered ? "bold" : "normal"} color="primary">{key}</Text>
                </View>
              ))}
            </View>
          </>
        );
      }}
    </Pressable>
  );
}

function KeyboardShortcutSettings() {
  const shortcuts = Object.values(SHORTCUTS).filter(shortcut => shortcut.group !== 'work');
  const workShortcuts = Object.values(SHORTCUTS).filter(shortcut => shortcut.group === 'work');
  const isMac = Platform.OS === 'macos' || (Platform.OS === 'web' && typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform));
  const displayKeysFor = shortcut => ((Platform.OS === 'macos' && shortcut.macKeys) ? shortcut.macKeys : shortcut.keys)
    .map(key => !isMac && key === '⌘' ? 'Ctrl' : !isMac && key === '⌥' ? 'Alt' : key);
  const { colors }= useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const DECK_SHORTCUTS = useMemo(() => [
    { action: t('settings.shortcuts.addNewColumn'), keys: ['⌘', '⇧', 'N'] },
    { action: t('settings.shortcuts.removeFocusedColumn'), keys: ['⌘', '⇧', 'W'] },
    { action: t('settings.shortcuts.focusColumn'), keys: ['⌘', '1-9'] },
    { action: t('settings.shortcuts.moveColumnLeft'), keys: ['⌘', '⇧', '['] },
    { action: t('settings.shortcuts.moveColumnRight'), keys: ['⌘', '⇧', ']'] },
  ], [t]);

  return (
    <View style={styles.container}>
      <SettingsSection
        title={t('settings.shortcuts.general')}
        isBold
        isColumn
        isFirst
      >
        {shortcuts.map((shortcut, index) => {
          const displayKeys = displayKeysFor(shortcut);
          return (
            <ShortcutRow
              key={index}
              shortcut={shortcut}
              displayKeys={displayKeys}
              isFirst={index === 0}
              colors={colors}
              styles={styles}
              t={t}
            />
          );
        })}
        <ShortcutRow
          shortcut={{ action: 'shortcuts.findInChat' }}
          displayKeys={displayKeysFor({ keys: ['⌘', 'F'] })}
          isFirst={false}
          colors={colors}
          styles={styles}
          t={t}
        />
      </SettingsSection>
      <SettingsSection
        title={t('settings.shortcuts.work')}
        subtitle={t('settings.shortcuts.workDescription')}
        isBold
        isColumn
      >
        {workShortcuts.map((shortcut, index) => <ShortcutRow
          key={shortcut.action} shortcut={shortcut} displayKeys={displayKeysFor(shortcut)}
          isFirst={index === 0} colors={colors} styles={styles} t={t}
        />)}
      </SettingsSection>
      <SettingsSection
        title={t('settings.shortcuts.deck')}
        isBold
        isColumn
        isLast
      >
        {DECK_SHORTCUTS.map((shortcut, index) => (
          <ShortcutRow
            key={`deck-${index}`}
            shortcut={shortcut}
            displayKeys={displayKeysFor(shortcut)}
            isFirst={index === 0}
            colors={colors}
            styles={styles}
            t={t}
          />
        ))}
      </SettingsSection>
    </View>
  );
} 

export default React.memo(KeyboardShortcutSettings);